import { publish, subscribe } from '@rei/event-manager';
import jsCookie from 'js-cookie';

const REI_SESSION_USER_CHANGED = 'rei_session_user_changed';

const some = (array, predicate) => {
  if (!array) return false;

  for (let i = 0; i < array.length; i += 1) {
    if (predicate(array[i])) return true;
  }

  return false;
};

const STORE = 'myStoreId';
const STORE_SET = 'myStoreIdSet';
const storeCookieOpts = { expires: 365 };
export const setCookieStore = (userStore) => {
  if (userStore) {
    jsCookie.set(STORE, userStore.id, storeCookieOpts);
    jsCookie.set(STORE_SET, userStore.lastSet, storeCookieOpts);
  } else {
    jsCookie.remove(STORE);
    jsCookie.remove(STORE_SET);
  }
};

export const getCookieStore = () => {
  const cookieStoreId = jsCookie.get(STORE);
  const cookieStoreSet = jsCookie.get(STORE_SET);
  return (
    cookieStoreId
    && cookieStoreSet
    && { id: cookieStoreId, lastSet: cookieStoreSet }
  ) || null;
};

export const patchPreferredStore = async (storeId) => {
  const res = await fetch(
    '/rest/user/preferences',
    {
      method: 'PUT',
      headers: {
        Accept: 'application/json;v=2',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: storeId }),
      credentials: 'same-origin',
    },
  );

  if (!res.ok) throw new Error();

  return res.json();
};

const nullUser = {
  accountId: null,
  isLoggedIn: false,
  addresses: [],
  membership: null,
  contact: null,
  preferredStore: null,
};

const fetchUser = async () => {
  const res = await fetch(
    '/rest/user/account',
    { credentials: 'same-origin' },
  );

  const json = await res.json();
  if (res.ok) {
    return {
      isLoggedIn: true,
      ...json,
      membership: json.membership || null,
    };
  }

  const notLoggedInCode = {
    400: 1424,
    401: 1402,
  }[res.status];

  if (
    notLoggedInCode
    && some(json?.error, (e) => e.code === notLoggedInCode)
  ) {
    return nullUser;
  }

  throw new Error();
};

// There's a possibility that an application can call getUser before getUser
// is loaded onto the window. Applications can use addChangeListener
// to subscribe to any updates to the user and they will get notified
// when a change has occured.
const addChangeListener = (fn) => subscribe(REI_SESSION_USER_CHANGED, (e) => fn(e.detail.user));

export default class UserService {
  constructor() {
    this.getUser = this.getUser.bind(this);
    this.setPreferredStore = this.setPreferredStore.bind(this);
    if (window && !window.reiSession) {
      window.reiSession = {
        getUser: this.getUser,
        setPreferredStore: this.setPreferredStore,
        addChangeListener,
      };
    }
    this.userPromise = null;
  }

  getUser() {
    this.userPromise = this.userPromise || fetchUser().then(async (user) => {
      const userStore = user.preferredStore;
      const cookieStore = getCookieStore();

      let preferredStore = cookieStore;

      if (user.isLoggedIn) {
        if ((userStore?.lastSet || 0) < (cookieStore?.lastSet || 0)) {
          preferredStore = await patchPreferredStore(cookieStore.id);
        } else {
          preferredStore = userStore;
        }

        setCookieStore(preferredStore);
      }

      return { ...user, preferredStore };
    }).catch((error) => error);

    return this.userPromise;
  }

  async emitChange(user) {
    this.userPromise = Promise.resolve(this.userPromise)
      .catch(() => {})
      .then(() => {
        publish(REI_SESSION_USER_CHANGED, {
          detail: {
            user,
          },
        });
        return user;
      });

    await this.userPromise;
  }

  async setPreferredStore(storeId) {
    const user = await window.reiSession.getUser();
    const preferredStore = user.isLoggedIn
      ? await patchPreferredStore(storeId)
      : (storeId && { id: storeId, lastSet: +new Date() });

    setCookieStore(preferredStore);

    const newUser = {
      ...user,
      preferredStore,
    };

    return this.emitChange(newUser);
  }
}
// eslint-disable-next-line
new UserService();